import { useState, useEffect } from "react";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Title } from "../../../../themes/Default/components/Title";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Title as ListTitle } from "../../../../themes/Default/components/List/components/Header/components/Title";
import { Checkbox } from "../../../../themes/Default/components/Form/components/Checkbox";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { useTranslation } from "react-i18next";
import { Color } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell/components/Color";
import { Toaster } from "../../../../themes/Default/components/Toaster";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";
const Notes = () => {
    const { t } = useTranslation("common");
    const { groupId } = useParams();
    const navigate = useNavigate();
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedNotes, setSelectedNotes] = useState([]);
    const [notes, setNotes] = useState();
    const { useNotesListMutation, useDeleteNoteMutation, useViewGroupMutation } = groupsManagement;
    const [notesList, { isLoading }] = useNotesListMutation();
    const [deleteNote] = useDeleteNoteMutation();
    const [totalRecords, setTotalRecords] = useState(0);
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);
    const [viewGroup] = useViewGroupMutation();

    const tabs = [
        { title: t("groups.attendance"), onClick: () => gotoAttendanceTab(groupId) },
        ...(hasPlan ? [
            { title: t("groups.planning"), onClick: () => gotoPlanningTab(groupId) },
        ] : []), 
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []), 
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []), 
        ...(hasReport ? [
            { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
        ] : []),
        { title: t("documents"), onClick: () => gotoDocumentsTab(groupId) },
        { title: t("notes"), isActive: true },
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    };

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    };

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    };

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    };

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    };

    const gotoNew = () => {
        navigate(`/groups/${groupId}/notes/add`);
    };

    const onSelectionChange = (id) => {
        setSelectedNotes((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedNotes?.length; i++) {
                            const noteId = selectedNotes[i];
                            deleteNote({ groupId, noteId }).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    notesList(groupId).then((result) => {
                                        const notes = result?.data?.records;
                                        setNotes(notes);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
            },
        );
    };

    useEffect(() => {
        if (selectedNotes.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedNotes]);

    useEffect(() => {
        viewGroup(groupId).then((result) => {
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
        });
        notesList(groupId).then((result) => {
            const notes = result?.data?.records;
            setNotes(notes);
            setTotalRecords(result?.data?.totalRecords);
        });
    }, [notesList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/groups" />
                <Button title={t("students.addNotes")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("notes")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-user-group" />
                    <Link title={t("notes")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader></ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("test.date")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("noteTypes.color")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("groups.noteType")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("plans.titlePlan")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("calendar.description")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {notes?.length > 0 &&
                                        notes?.map((note, index) => {
                                            return (
                                                <TableRow key={note.id}>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={note.id} onChange={() => onSelectionChange(note.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>{note.date}</TableDataCell>
                                                    <TableDataCell>
                                                        <Color color={note?.typeColor} />
                                                    </TableDataCell>
                                                    <TableDataCell>{note.typeName}</TableDataCell>
                                                    <TableDataCell>{note.title}</TableDataCell>
                                                    <TableDataCell>{note.description}</TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Notes;
